import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { appVersionApi, type AppVersionUpdate } from '@/api/appVersion'
import { CLIENT_VERSION } from '@/utils/systemHeader'

const DISMISS_STORAGE_KEY = 'chaotuishou_aigc_app_update_dismiss_date'
const RELOAD_ATTEMPT_KEY = 'chaotuishou_aigc_app_update_reload_attempt'
const MAX_RELOAD_ATTEMPTS = 5

const emptyUpdate = (): AppVersionUpdate => ({
  appVersionId: 0,
  platformType: 3,
  versionNumber: '',
  minimumRequiredVersionNumber: '',
  forceUpdate: false,
  featureIntroduction: '',
  characteristicIntroduction: '',
  detailImages: [],
  channel: null,
})

/** 四段版本号比较：返回 1 / 0 / -1 */
export const compareVersion = (left: string, right: string): number => {
  const parse = (value: string): number[] =>
    value.split('.').map((part) => {
      const n = Number.parseInt(part, 10)
      return Number.isFinite(n) && n >= 0 ? n : 0
    })

  const a = parse(left)
  const b = parse(right)
  const len = Math.max(a.length, b.length, 1)
  for (let i = 0; i < len; i++) {
    const na = a[i] ?? 0
    const nb = b[i] ?? 0
    if (na > nb) return 1
    if (na < nb) return -1
  }
  return 0
}

const getLocalDateKey = (): string => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const readDismissedDate = (): string => {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem(DISMISS_STORAGE_KEY)?.trim() || ''
}

const readReloadAttempt = (): number => {
  if (typeof window === 'undefined') return 0
  const raw = sessionStorage.getItem(RELOAD_ATTEMPT_KEY)
  const n = Number.parseInt(raw || '0', 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

const clearReloadAttempt = () => {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
}

const normalizeUpdate = (value: unknown): AppVersionUpdate | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const raw = value as AppVersionUpdate
  const versionNumber = typeof raw.versionNumber === 'string' ? raw.versionNumber.trim() : ''
  if (!versionNumber) return null
  return {
    appVersionId: Number.isFinite(Number(raw.appVersionId)) ? Number(raw.appVersionId) : 0,
    platformType: Number.isFinite(Number(raw.platformType)) ? Number(raw.platformType) : 3,
    versionNumber,
    minimumRequiredVersionNumber:
      typeof raw.minimumRequiredVersionNumber === 'string' ? raw.minimumRequiredVersionNumber : '',
    forceUpdate: Boolean(raw.forceUpdate),
    featureIntroduction: typeof raw.featureIntroduction === 'string' ? raw.featureIntroduction : '',
    characteristicIntroduction:
      typeof raw.characteristicIntroduction === 'string' ? raw.characteristicIntroduction : '',
    detailImages: Array.isArray(raw.detailImages) ? raw.detailImages : [],
    channel: raw.channel && typeof raw.channel === 'object' ? raw.channel : null,
  }
}

const bustBrowserCache = async () => {
  if (typeof window === 'undefined') return

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((reg) => reg.unregister()))
    }
  } catch (error) {
    console.warn('[appVersionStore] 注销 Service Worker 失败:', error)
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch (error) {
    console.warn('[appVersionStore] 清理 Cache Storage 失败:', error)
  }

  const stamp = Date.now()
  const origin = window.location.origin
  const urls = [`${origin}/?_cts=${stamp}`, `${origin}/index.html?_cts=${stamp}`]
  await Promise.all(
    urls.map((url) =>
      fetch(url, {
        cache: 'reload',
        credentials: 'same-origin',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      }).catch(() => undefined)
    )
  )
}

const reloadPage = () => {
  if (typeof window === 'undefined') return
  window.location.reload()
}

export const useAppVersionStore = defineStore('appVersion', () => {
  const update = ref<AppVersionUpdate>(emptyUpdate())
  const dismissedDate = ref(readDismissedDate())
  const closedInSession = ref(false)
  const applying = ref(false)

  let inflight: Promise<boolean> | null = null

  const hasNewerVersion = computed(
    () => compareVersion(update.value.versionNumber, CLIENT_VERSION) > 0
  )

  const shouldOfferUpdate = computed(() => {
    if (!update.value.versionNumber) return false
    return update.value.forceUpdate || hasNewerVersion.value
  })

  /** 控制新版本更新组件是否展示 */
  const visible = computed(() => {
    if (!shouldOfferUpdate.value) return false
    if (update.value.forceUpdate) {
      return !closedInSession.value
    }
    return dismissedDate.value !== getLocalDateKey()
  })

  const reset = () => {
    inflight = null
    update.value = emptyUpdate()
    closedInSession.value = false
    applying.value = false
    dismissedDate.value = readDismissedDate()
  }

  /** 关闭新版本更新组件 */
  const dismiss = () => {
    if (!shouldOfferUpdate.value) return
    if (update.value.forceUpdate) {
      closedInSession.value = true
      return
    }
    const today = getLocalDateKey()
    if (typeof window !== 'undefined') {
      localStorage.setItem(DISMISS_STORAGE_KEY, today)
    }
    dismissedDate.value = today
  }

  const continuePendingReload = async () => {
    const attempt = readReloadAttempt()
    if (attempt <= 0) return

    if (!visible.value) {
      clearReloadAttempt()
      return
    }

    if (attempt >= MAX_RELOAD_ATTEMPTS) {
      console.warn('[appVersionStore] 已达最大刷新次数，停止自动刷新以避免缓存导致死循环')
      clearReloadAttempt()
      applying.value = false
      return
    }

    applying.value = true
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(attempt + 1))
    }
    await bustBrowserCache()
    reloadPage()
  }

  /** 立即更新：清缓存后刷新，直到组件不再展示或达到上限 */
  const applyUpdate = async () => {
    if (applying.value) return
    applying.value = true
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(RELOAD_ATTEMPT_KEY, '1')
    }
    await bustBrowserCache()
    reloadPage()
  }

  /** 拉取可用更新，可供任意地方调用；页面刷新时会自动执行 */
  const fetchUpdate = async (): Promise<boolean> => {
    if (inflight) {
      return inflight
    }

    inflight = (async () => {
      try {
        const res = await appVersionApi.queryUpdate()
        if (res?.code !== 0) {
          console.error('[appVersionStore] 查询更新版本失败:', res?.message)
          update.value = emptyUpdate()
          return false
        }
        update.value = normalizeUpdate(res.data) || emptyUpdate()
        await continuePendingReload()
        return true
      } catch (error) {
        console.error('[appVersionStore] 查询更新版本失败:', error)
        update.value = emptyUpdate()
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  return {
    update,
    visible,
    applying,
    clientVersion: CLIENT_VERSION,
    fetchUpdate,
    dismiss,
    applyUpdate,
    reset,
  }
})
